import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ShieldCheck, Zap, Globe2, BadgePercent } from "lucide-react";
import { api, formatMoney } from "../lib/api";
import Compare from "../components/Compare";
import Ticker from "../components/Ticker";

const HERO_IMG = "https://images.pexels.com/photos/29801855/pexels-photo-29801855.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRUST_IMG = "https://images.unsplash.com/photo-1724453796952-f7df6d7e289a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const Feature = ({ icon: Icon, title, desc, testid }) => (
  <div className="ra-card p-6" data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[var(--ra-primary)]/8 flex items-center justify-center mb-4 text-[var(--ra-primary)]">
      <Icon size={20} />
    </div>
    <h3 className="font-heading text-lg font-semibold mb-1.5">{title}</h3>
    <p className="text-sm text-[var(--ra-muted)] leading-relaxed">{desc}</p>
  </div>
);

const Landing = () => {
  const [rates, setRates] = useState([]);
  useEffect(() => { api.get("/fx/rates").then(r => setRates(r.data.rates.slice(0, 6))).catch(() => {}); }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 mudcloth-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--ra-border)] bg-white/80 text-xs uppercase tracking-[0.2em] text-[var(--ra-primary)] font-semibold mb-6" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--ra-accent)]" />
              Trusted by families across 8 countries
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
              Send money home <br className="hidden sm:block"/>
              <span className="text-[var(--ra-primary)]">in minutes,</span> not days.
            </h1>
            <p className="text-base sm:text-lg text-[var(--ra-muted)] max-w-xl mb-4 leading-relaxed">
              Lightning-fast, low-fee transfers to Kenya, Ghana, South Africa, Nigeria and beyond. M-Pesa, mobile money, bank deposit & cash pickup — all from one place.
            </p>
            <p className="text-sm text-[var(--ra-accent)] font-semibold mb-8 italic" data-testid="hero-tagline">
              ✦ Zero fees. Clear rates. A clear way home for every dollar.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="btn-primary rounded-md px-7 h-12" data-testid="hero-cta-start">
                Start sending <ArrowRight size={16} className="ml-2" />
              </Button></Link>
              <Link to="/rates"><Button size="lg" variant="outline" className="rounded-md px-7 h-12 border-[var(--ra-primary)] text-[var(--ra-primary)] hover:bg-[var(--ra-primary)]/5" data-testid="hero-cta-rates">
                See live rates
              </Button></Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[var(--ra-muted)]">
              <div className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-[var(--ra-success)]" /> Bank-grade encryption</div>
              <div className="flex items-center gap-1.5"><BadgePercent size={16} className="text-[var(--ra-accent)]" /> Zero fees</div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-2">
            <div className="ra-card overflow-hidden p-0">
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img src={HERO_IMG} alt="Family connected through ClearPurse Money" className="w-full h-full object-cover" />
              </div>
              <div className="p-5 border-t border-[var(--ra-border)]">
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-muted)] font-semibold">Today's rate</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-heading text-2xl font-bold">1 USD</span>
                  <span className="text-[var(--ra-muted)]">=</span>
                  <span className="font-heading text-2xl font-bold text-[var(--ra-primary)]">1,485.50 NGN</span>
                </div>
                <div className="text-xs text-[var(--ra-muted)] mt-1">No hidden markups · Updated live</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FX strip */}
      {rates.length > 0 && (
        <section className="border-y border-[var(--ra-border)] bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {rates.map((r) => (
              <div key={r.country_code} className="flex items-center gap-3" data-testid={`fx-strip-${r.country_code}`}>
                <span className="text-2xl">{r.flag}</span>
                <div>
                  <div className="text-xs text-[var(--ra-muted)] uppercase tracking-wider">{r.currency}</div>
                  <div className="font-heading font-semibold">{formatMoney(r.rate, r.currency)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Features tetris */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-3">Why ClearPurse</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Built for the families who keep two continents connected.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <Feature icon={Zap} title="Arrives in minutes" desc="Most transfers settle in under 5 minutes via M-Pesa, mobile money or instant bank rails." testid="feature-speed" />
          </div>
          <div className="md:col-span-7">
            <div className="ra-card overflow-hidden p-0 h-full" data-testid="feature-trust-img">
              <div className="grid grid-cols-5 h-full">
                <div className="col-span-3">
                  <img src={TRUST_IMG} alt="Family connection" className="w-full h-full object-cover" />
                </div>
                <div className="col-span-2 p-6 flex flex-col justify-center">
                  <h3 className="font-heading text-xl font-semibold mb-2">For loved ones</h3>
                  <p className="text-sm text-[var(--ra-muted)]">Reach 9 African countries with one trusted account. Save recipients once, send instantly forever.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-4">
            <Feature icon={ShieldCheck} title="KYC-protected" desc="Identity verified, end-to-end encrypted. We follow strict AML & compliance standards." testid="feature-secure" />
          </div>
          <div className="md:col-span-4">
            <Feature icon={Globe2} title="9 countries" desc="Nigeria, Kenya, Ghana, South Africa, Uganda, Tanzania, Senegal, Cameroon, South Sudan." testid="feature-countries" />
          </div>
          <div className="md:col-span-4">
            <Feature icon={BadgePercent} title="Zero fees" desc="No transfer fees, ever. What you send is what they receive." testid="feature-fees" />
          </div>
        </div>
      </section>

      {/* Compare vs banks/WU */}
      <Compare />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="ra-card p-10 sm:p-14 bg-[var(--ra-primary)] text-white border-[var(--ra-primary)]">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">Your first transfer is on us.</h2>
              <p className="text-white/80 max-w-md">Sign up in 2 minutes, verify your ID, and send up to $500 fee-free.</p>
            </div>
            <div className="flex md:justify-end">
              <Link to="/register"><Button size="lg" className="bg-[var(--ra-accent)] hover:bg-[#B85240] text-white rounded-md px-8 h-12" data-testid="cta-register-btn">
                Create free account <ArrowRight size={16} className="ml-2" />
              </Button></Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--ra-border)] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 text-sm text-[var(--ra-muted)] flex justify-between flex-wrap gap-3">
          <span>© 2026 ClearPurse Money · Demo for educational purposes</span>
          <Link to="/brand" className="hover:text-[var(--ra-primary)]" data-testid="footer-brand-link">Brand kit</Link>
          <span>Powered by Stripe · M-Pesa · Mobile Money</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
